import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, Select, Table } from 'antd';
import styled from 'styled-components';
import Button from '~/modules/components/global/button';
import Icon from '~/modules/components/global/icon';
import EmptyState from '~/modules/components/global/empty-state';
import {
	TableStyles
} from '~/modules/components/share-feedback/share-feedback-report/share-feedback-report.css';
import { Label } from '~/pages/common.css';
import confirm from '~/modules/components/global/confirm';
import ToastService from '~/modules/services/toast-service';
import ServiceAccountService from '~/modules/services/service-account-service';
import useAuth from '~/modules/hooks/use-auth';
import ServiceAccountTokens from './service-account-tokens';
import { captureError } from '../../../pixwel/exception-handler.factory';

const QUERY_KEY = ['serviceAccounts'];

const ServiceAccountsContainer = styled.div`
	min-height: fit-content;

	fieldset {
		margin-bottom: 2rem;
	}

	.warning-copy {
		font-size: 0.85rem;
		color: ${({ theme }) => theme['silver-base']};
		margin-bottom: 1rem;
	}

	.create-form {
		display: flex;
		gap: 0.75rem;
		align-items: flex-end;
		margin-bottom: 1.5rem;
		flex-wrap: wrap;
	}

	.create-form > div {
		display: flex;
		flex-direction: column;
	}

	.create-form .name-field {
		flex: 1 1 14rem;
	}

	.create-form .group-field {
		flex: 0 0 14rem;
	}

	.create-form .expiry-field {
		flex: 0 0 10rem;
	}

	.rotation-hint {
		font-size: 0.85rem;
		color: ${({ theme }) => theme['silver-base']};
		margin: 0.5rem 0 1rem;
	}

	.disabled-row {
		opacity: 0.6;
	}
`;

/**
 * The groups whose service accounts this person may manage, as `{ label, value }` options.
 *
 * Read straight off the session rather than derived from permissions: the API computes it in
 * `Permissions::serviceAccountGroups()` and sends it as `serviceAccountGroups`, so there is one rule
 * in one place. The API re-checks every request regardless, so a stale session yields a 403 rather
 * than unauthorised access.
 */
export const manageableGroups = (session) => (session?.serviceAccountGroups || [])
	.map((group) => ({
		label: group?.name || String(group?._id || group?.id),
		value: String(group?._id || group?.id)
	}))
	.filter((option) => option.value && option.value !== 'undefined');

const ServiceAccounts = () => {
	const queryClient = useQueryClient();
	const { session } = useAuth();

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [group, setGroup] = useState(null);

	const groupOptions = useMemo(() => manageableGroups(session), [session]);

	// With a single manageable group there is nothing to choose, so don't make anyone choose it.
	const selectedGroup = group || (groupOptions.length === 1 ? groupOptions[0].value : null);

	const { data: accounts = [], isLoading } = useQuery(
		QUERY_KEY,
		() => ServiceAccountService.list(),
		{ refetchOnWindowFocus: false }
	);

	const { mutateAsync: createAccount, isLoading: isCreating } = useMutation(
		(payload) => ServiceAccountService.create(payload),
		{
			onSuccess: (account) => {
				queryClient.invalidateQueries(QUERY_KEY);
				setName('');
				setDescription('');
				setGroup(null);
				ToastService.create('success', `Service account "${account.name}" created`);
			},
			onError: (err) => {
				const message = err?.response?.data?.error || err.message || 'Failed to create service account';
				ToastService.create('error', message);
				captureError(err, `Error creating service account: ${err.message}`);
			}
		}
	);

	const { mutateAsync: setDisabled } = useMutation(
		({ id, disabled }) => (
			disabled
				? ServiceAccountService.disable(id)
				: ServiceAccountService.update(id, { disabled: false })
		),
		{
			onSuccess: (_result, { id, disabled }) => {
				queryClient.invalidateQueries(QUERY_KEY);

				// Disabling revokes every token server-side. If this row is expanded its token list is
				// still mounted, and window-focus refetching is off, so without this it would keep
				// presenting revoked tokens as active — including after a re-enable, which does not
				// bring them back.
				queryClient.invalidateQueries(['serviceAccountTokens', id]);

				ToastService.create('success', disabled ? 'Service account disabled' : 'Service account enabled');
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to update service account');
				captureError(err, `Error updating service account: ${err.message}`);
			}
		}
	);

	const handleCreate = async() => {
		const trimmed = name.trim();

		if( !trimmed || !selectedGroup ) {
			return;
		}

		await createAccount({ name: trimmed, group: selectedGroup, description: description.trim() });
	};

	const handleDisable = async(row) => {
		const ok = await confirm({
			title: 'Disable service account',
			content: `Disable "${row.name}"? Its tokens are revoked immediately and every integration `
				+ 'using them stops working. Re-enabling does not restore them.',
			confirmText: 'Disable',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		});

		if( !ok ) {
			return;
		}

		await setDisabled({ id: row._id, disabled: true });
	};

	const groupName = (id) => {
		const match = groupOptions.find((option) => option.value === String(id));
		return match ? match.label : id;
	};

	const columns = [
		{ title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
		{
			title: 'Group',
			dataIndex: 'group',
			key: 'group',
			render: (value) => groupName(value)
		},
		{ title: 'Purpose', dataIndex: 'description', key: 'description', ellipsis: true },
		{
			title: 'Status',
			dataIndex: 'disabledAt',
			key: 'disabledAt',
			width: 110,
			render: (disabledAt) => (disabledAt ? 'Disabled' : 'Active')
		},
		{
			title: 'Action',
			key: 'action',
			width: 140,
			render: (_value, row) => (
				row.disabledAt
					? (
						<Button
							variant="secondary"
							data-testid="service-account-enable-button"
							onClick={() => setDisabled({ id: row._id, disabled: false })}
						>
							Enable
						</Button>
					)
					: (
						<Button
							variant="danger"
							data-testid="service-account-disable-button"
							onClick={() => handleDisable(row)}
						>
							<Icon use="trash" color="white" /> Disable
						</Button>
					)
			)
		}
	];

	return (
		<ServiceAccountsContainer data-testid="preferences-service-accounts">
			<fieldset>
				<legend className="h5">Create service account</legend>
				<p className="warning-copy">
					Service accounts belong to a group, not to you. They keep working when people join or
					leave, and their access is whatever the owning group has been granted. Use them for
					integrations; use API tokens for your own scripts.
				</p>
				<div className="create-form">
					<div className="name-field">
						<Label>Name</Label>
						<Input
							placeholder="e.g. Nightly Sync"
							value={name}
							onChange={(e) => setName(e.target.value)}
							data-testid="service-account-name-input"
						/>
					</div>
					{groupOptions.length > 1 && (
						<div className="group-field">
							<Label>Group</Label>
							<Select
								style={{ width: '100%' }}
								value={selectedGroup}
								onChange={setGroup}
								options={groupOptions}
								placeholder="Select a group"
								data-testid="service-account-group-select"
							/>
						</div>
					)}
					<div className="name-field">
						<Label>Purpose</Label>
						<Input
							placeholder="What will use this?"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							data-testid="service-account-description-input"
						/>
					</div>
					<div>
						<Button
							onClick={handleCreate}
							loading={isCreating}
							disabled={!name.trim() || !selectedGroup || isCreating}
							data-testid="service-account-create-button"
						>
							Create service account
						</Button>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<legend className="h5">Service accounts</legend>
				<TableStyles>
					<Table
						columns={columns}
						dataSource={accounts.map((account) => ({ ...account, key: account._id }))}
						pagination={false}
						loading={isLoading}
						rowClassName={(row) => (row.disabledAt ? 'disabled-row' : '')}
						expandable={{
							expandedRowRender: (row) => <ServiceAccountTokens account={row} />,
							rowExpandable: () => true
						}}
						locale={{ emptyText: <EmptyState message="No service accounts" /> }}
					/>
				</TableStyles>
			</fieldset>
		</ServiceAccountsContainer>
	);
};

export default ServiceAccounts;
